// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import { useVaccineDashboardStore } from '../../../store/vaccineDashboardStore';
import { REGIONS } from '../lib/models';
import { RegionalCoverageCard } from './RegionalCoverageCard';

/**
 * Connected wrapper component for RegionalCoverageCard
 * Connects to Zustand store and uses derived hooks for computed values
 */
export const RegionalCoverageCardConnected: React.FC = () => {
  const inputs = useVaccineDashboardStore((state) => state.inputs);
  const regional = useVaccineDashboardStore((state) => state.regional);
  const nationalCoveragePct = useVaccineDashboardStore((state) => state.globalInputs.nationalCoveragePct);
  const handleCoverageChange = useVaccineDashboardStore((state) => state.handleCoverageChange);
  const handleRegionToggle = useVaccineDashboardStore((state) => state.handleRegionToggle);
  const handleRegionsChange = useVaccineDashboardStore((state) => state.handleRegionsChange);
  const setNationalCoveragePct = useVaccineDashboardStore((state) => state.setNationalCoveragePct);
  const patchInputs = useVaccineDashboardStore((state) => state.patchInputs);
  const setSelectedRegion = useVaccineDashboardStore((state) => state.setSelectedRegion);

  const selectedRegionId = regional.selectedRegionId || 'finland';
  const selectedRegion = REGIONS.find(r => r.key === selectedRegionId);
  const selectedRegionName = selectedRegion?.name || 'Unknown region';
  
  // Calculate coverage for display: average if multiple regions, otherwise use selected region's coverage
  const selectedRegionCoverage = (() => {
    if (inputs.selectedRegions.includes('finland')) {
      return nationalCoveragePct;
    }
    if (inputs.selectedRegions.length === 0) {
      return regional.defaultCoveragePct;
    }
    if (inputs.selectedRegions.length === 1) {
      const regionKey = inputs.selectedRegions[0];
      return inputs.coveragePctByRegion[regionKey] ?? regional.defaultCoveragePct;
    }
    // Multiple regions: calculate average coverage
    const coverages = inputs.selectedRegions
      .filter(key => key !== 'finland')
      .map(key => inputs.coveragePctByRegion[key] ?? regional.defaultCoveragePct);
    if (coverages.length === 0) {
      return regional.defaultCoveragePct;
    }
    const sum = coverages.reduce((acc, val) => acc + val, 0);
    return sum / coverages.length;
  })();

  const handleDefaultCoverageChange = (value: number) => {
    patchInputs({ defaultCoveragePct: value });
  };

  const onCoverageChange = (value: number) => {
    if (inputs.selectedRegions.includes('finland')) {
      setNationalCoveragePct(value);
    } else {
      // Apply coverage to all selected regions
      inputs.selectedRegions.forEach(regionKey => {
        if (regionKey !== 'finland') {
          handleCoverageChange(regionKey, value);
        }
      });
    }
  };

  const onRegionToggle = (regionKey: string) => {
    handleRegionToggle(regionKey);
    setSelectedRegion(regionKey);
  };

  return (
    <RegionalCoverageCard
      selectedRegions={inputs.selectedRegions}
      selectedRegionId={selectedRegionId}
      selectedRegionName={selectedRegionName}
      selectedRegionCoverage={selectedRegionCoverage}
      nationalCoveragePct={nationalCoveragePct}
      coverageByRegion={inputs.coveragePctByRegion}
      defaultCoveragePct={inputs.defaultCoveragePct}
      onCoverageChange={onCoverageChange}
      onRegionToggle={onRegionToggle}
      onRegionsChange={handleRegionsChange}
      onDefaultCoverageChange={handleDefaultCoverageChange}
    />
  );
};
